import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Container,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
  Fade,
  Avatar,
  Divider,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  Star as StarIcon,
  Translate as TranslateIcon,
  VolumeUp as VolumeIcon,
  Chat as ChatIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  TrendingUp as TrendingIcon,
  Handshake as HandshakeIcon,
  LinkedIn as LinkedInIcon,
  Language as GlobeIcon,
} from '@mui/icons-material';
import imghome from '../assests/about.jpg';
import wrk from '../assests/hands.jpg';
import logoImg from '../assests/logo.jpg';
// Import partner logos
import momulimisaLogo from '../assests/momulimisa.png';
import bodaaiLogo from '../assests/bodaai.png';
import quakeLogo from '../assests/quake.png';
import ctdruLogo from '../assests/ctdru.png';
import Detail from "./Detail";
import EmailFormCard from "./EmailFormCard";

// Import team members
import jonanImg from '../assests/1639396018202.jpeg';
import saulImg from '../assests/1711651920821-2.jpeg';
import brianImg from '../assests/1738663191129.jpeg';
import simonImg from '../assests/simon-photo.png';

const About = () => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const emailFormRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToEmailForm = () => {
    emailFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  };



  const features = [
    {
      icon: <TranslateIcon />,
      title: 'Machine Translation',
      description: 'Advanced AI-powered translation for African languages with cultural context preservation',
      color: '#F59E0B'
    },
    {
      icon: <VolumeIcon />,
      title: 'Voice Technology',
      description: 'TTS and ASR systems designed specifically for African accents and linguistic patterns',
      color: '#F59E0B'
    },
    {
      icon: <ChatIcon />,
      title: 'AI Chatbots',
      description: 'Intelligent conversational AI for agricultural, policy, and fraud detection domains',
      color: '#F59E0B'
    },
    {
      icon: <BusinessIcon />,
      title: 'Automated Data Entry & OCR',
      description: 'AI-powered optical character recognition and automated data extraction from documents and images',
      color: '#F59E0B'
    },
    {
      icon: <TrendingIcon />,
      title: 'Data Mining & Analytics',
      description: 'Advanced data mining techniques and analytics to extract valuable insights from complex datasets',
      color: '#F59E0B'
    },
    {
      icon: <SchoolIcon />,
      title: 'Data Curation & Management',
      description: 'Comprehensive data curation, validation, and management solutions for structured and unstructured data',
      color: '#F59E0B'
    }
  ];

  const partners = [
    {
      name: "M-Omulimisa",
      description: "AI-powered agricultural extension services and mobile technology solutions for smallholder farmers",
      website: "https://m-omulimisa.com",
      logo: momulimisaLogo,
      color: '#F59E0B',
      category: "Agriculture"
    },
    {
      name: "Boda AI Factory",
      description: "AI and Data Science solutions for African organizations across agriculture, healthcare, and finance",
      website: "http://www.boda-ai-factory.com",
      logo: bodaaiLogo,
      color: '#F59E0B',
      category: "AI & Data Science"
    },
    {
      name: "Quake",
      description: "Creative advertising and brand solutions with innovative problem-solving approach",
      website: "https://quakebrandfix.com",
      logo: quakeLogo,
      color: '#F59E0B',
      category: "Creative & Advertising"
    },
    {
      name: "CTDRU",
      description: "Uganda's leading virtual technology hub for consumer protection and digital rights",
      website: "https://ctdrug.org",
      logo: ctdruLogo,
      color: '#F59E0B',
      category: "Technology & Rights"
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box sx={{
        minHeight: '70vh',
        backgroundColor: '#f8fafc',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}>
        {/* Clean Corporate Grid Background */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          backgroundImage: `radial-gradient(#9CA3AF 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          zIndex: 0,
        }} />

        {/* Transparent Logo Background Left */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '-10%',
          transform: 'translateY(-50%)',
          height: '90vh',
          width: '50vw',
          backgroundImage: `url(${logoImg})`,
          backgroundSize: 'contain',
          backgroundPosition: 'left center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.07,
          zIndex: 1,
          mixBlendMode: 'multiply'
        }} />

        {/* Transparent Logo Background Right */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          right: '-10%',
          transform: 'translateY(-50%)',
          height: '90vh',
          width: '50vw',
          backgroundImage: `url(${logoImg})`,
          backgroundSize: 'contain',
          backgroundPosition: 'right center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.07,
          zIndex: 1,
          mixBlendMode: 'multiply'
        }} />

        {/* Premium Blur Glows */}
        <Box sx={{
          position: 'absolute',
          top: '-20%',
          left: '10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(245,158,11, 0.04) 0%, transparent 60%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-20%',
          right: '10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(245,158,11, 0.04) 0%, transparent 60%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }} />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center" sx={{ minHeight: '70vh' }}>
            <Grid item xs={12} lg={12}>
              <Fade in={isVisible} timeout={1000}>
                <Box sx={{ color: '#111827', textAlign: 'center' }}>
                  {/* Badge */}
                  <Chip
                    icon={<StarIcon />}
                    label="Our Story"
                    sx={{
                      backgroundColor: '#F3F4F6',
                      color: '#4B5563',
                      border: '1px solid #E5E7EB',
                      fontWeight: 700,
                      mb: 3,
                      px: 2,
                      py: 1,
                      borderRadius: '20px',
                      '& .MuiChip-icon': { color: '#F59E0B' }
                    }}
                  />

                  {/* Main Title */}
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '2.8rem', sm: '3.8rem', md: '4.5rem', lg: '5.5rem' },
                      lineHeight: 1.05,
                      letterSpacing: '-0.04em',
                      mb: 2.5,
                      color: '#111827',
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    ABOUT PHOSAI
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.2rem', sm: '1.45rem', md: '1.75rem' },
                      lineHeight: 1.35,
                      color: '#4B5563',
                      mb: 4,
                      maxWidth: 600,
                      mx: 'auto',
                    }}
                  >
                    Pioneering AI and Machine Translation for African Languages
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      lineHeight: 1.75,
                      color: '#6B7280',
                      mb: 5,
                      maxWidth: 660,
                      fontWeight: 500,
                      mx: 'auto',
                    }}
                  >
                    We are a world-class AI company developing cutting-edge language technology solutions
                    that bridge linguistic barriers across Africa — preserving culture while driving innovation.
                  </Typography>

                  {/* CTA Button */}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={scrollToEmailForm}
                    endIcon={<ArrowIcon />}
                    sx={{
                      background: '#F59E0B',
                      color: 'white',
                      px: 5,
                      py: 2,
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(245,158,11, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(245,158,11, 0.4)',
                        background: '#D97706',
                      }
                    }}
                  >
                    Get In Touch
                  </Button>
                </Box>
              </Fade>
            </Grid>

            
          </Grid>
        </Container>
      </Box>

      {/* Mission / Vision / Values Strip */}
      <Box sx={{
        py: { xs: 6, md: 8 },
        background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
        borderTop: '1px solid rgba(245,158,11, 0.15)',
        borderBottom: '1px solid rgba(245,158,11, 0.15)',
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {[
              {
                label: 'Our Mission',
                color: '#F59E0B',
                text: 'To democratize AI for African communities by building language technology that is accurate, culturally aware, and accessible to everyone — in their own language.',
              },
              {
                label: 'Our Vision',
                color: '#F59E0B',
                text: 'A continent where language is never a barrier to opportunity — where every African can access information, healthcare, education, and economic tools in their mother tongue.',
              },
              {
                label: 'Our Values',
                color: '#F59E0B',
                text: 'Cultural authenticity, technical excellence, inclusive design, and relentless innovation. We build AI that respects communities and drives measurable impact.',
              },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Fade in={isVisible} timeout={800 + i * 200}>
                  <Box sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: '20px',
                    background: `rgba(${item.color === '#F59E0B' ? '245,158,11' : item.color === '#F59E0B' ? '245,158,11' : '245,158,11'}, 0.07)`,
                    border: `1px solid ${item.color}25`,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)', border: `1px solid ${item.color}45` },
                  }}>
                    <Typography sx={{
                      fontWeight: 800,
                      fontSize: '0.75rem',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      color: item.color,
                      mb: 1.5,
                    }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#D1D5DB', lineHeight: 1.75, fontSize: '0.95rem', fontWeight: 500 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Main Content Section */}
      <Box sx={{
        py: { xs: 8, md: 12, lg: 16 },
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          {/* Detail Component */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Detail 
              description="PHOSAI is a pioneering AI and Machine Translation company developing cutting-edge language technology solutions for African languages. We specialize in machine translation for Luganda, Swahili, Kinyarwanda, Zulu, and Xhosa, creating bridges between communities and enabling effective communication across linguistic barriers." 
              description2="Our expertise extends beyond translation to include Text-to-Speech (TTS) systems, Automatic Speech Recognition (ASR) models, and intelligent chatbots designed specifically for the agricultural sector and policy domains. We understand the unique challenges of African languages and develop solutions that preserve cultural authenticity while delivering technological innovation."
              description3="Based at Makerere University in Kampala, Uganda, we leverage academic excellence and research capabilities to deliver world-class language technology solutions. Our strategic partnerships with M-OMUlimisa, MVETS, CTDRU, TourUg, and QUAKE enable us to serve diverse sectors including agriculture, veterinary services, tourism, and research, contributing to digital transformation across East Africa and beyond."
            />
          </Box>

          {/* Features Section */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip
                label="OUR EXPERTISE"
                sx={{
                  backgroundColor: 'rgba(245,158,11, 0.1)',
                  color: '#F59E0B',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  mb: 3,
                  px: 3,
                  py: 1.5,
                  borderRadius: '20px',
                }}
              />
              
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 900,
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  mb: 4,
                  color: '#111827',
                }}
              >
                Core Capabilities
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1.2rem',
                  lineHeight: 1.7,
                  color: '#6B7280',
                  maxWidth: 700,
                  margin: '0 auto',
                  fontWeight: 500,
                }}
              >
                Our comprehensive suite of AI-powered language technology and data processing solutions
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Fade in={isVisible} timeout={800 + index * 200}>
                    <Box sx={{
                      textAlign: 'center',
                      p: 4,
                      borderRadius: '24px',
                      background: 'white',
                      border: '1px solid rgba(245,158,11, 0.1)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(245,158,11, 0.15)',
                        borderColor: `${feature.color}30`,
                      }
                    }}>
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: `${feature.color}15`,
                        color: feature.color,
                        mb: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1) rotate(5deg)',
                          backgroundColor: `${feature.color}25`,
                        }
                      }}>
                        <Box sx={{ fontSize: 36, color: feature.color }}>
                          {feature.icon}
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 800,
                          color: '#111827',
                          mb: 2,
                          fontSize: '1.25rem',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#6B7280',
                          lineHeight: 1.7,
                          fontSize: '1rem',
                          fontWeight: 500,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Partners Section */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip
                icon={<HandshakeIcon />}
                label="STRATEGIC PARTNERSHIPS"
                sx={{
                  backgroundColor: 'rgba(245,158,11, 0.1)',
                  color: '#F59E0B',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  mb: 3,
                  px: 3,
                  py: 1.5,
                  borderRadius: '20px',
                  '& .MuiChip-icon': { color: '#F59E0B' }
                }}
              />
              
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 900,
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  mb: 4,
                  color: '#111827',
                }}
              >
                Our Partners
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1.2rem',
                  lineHeight: 1.7,
                  color: '#6B7280',
                  maxWidth: 700,
                  margin: '0 auto',
                  fontWeight: 500,
                }}
              >
                Collaborating with leading organizations to deliver innovative AI solutions across diverse sectors
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {partners.map((partner, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Fade in={isVisible} timeout={800 + index * 150}>
                    <Box sx={{
                      textAlign: 'center',
                      p: 4,
                      borderRadius: '24px',
                      background: 'white',
                      border: '1px solid rgba(245,158,11, 0.1)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(245,158,11, 0.15)',
                        borderColor: `${partner.color}30`,
                      }
                    }}
                    onClick={() => window.open(partner.website, '_blank')}
                    >
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 100,
                        height: 100,
                        borderRadius: '16px',
                        backgroundColor: 'white',
                        border: `2px solid ${partner.color}20`,
                        mb: 3,
                        transition: 'all 0.3s ease',
                        padding: 2,
                        '&:hover': {
                          transform: 'scale(1.05)',
                          borderColor: partner.color,
                          boxShadow: `0 8px 25px ${partner.color}30`,
                        }
                      }}>
                        <img 
                          src={partner.logo} 
                          alt={partner.name} 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            filter: 'grayscale(0%)',
                            transition: 'all 0.3s ease'
                          }} 
                        />
                      </Box>
                      
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 800,
                          color: '#111827',
                          mb: 1,
                          fontSize: '1.25rem',
                        }}
                      >
                        {partner.name}
                      </Typography>

                      <Chip
                        label={partner.category}
                        size="small"
                        sx={{
                          backgroundColor: `${partner.color}15`,
                          color: partner.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 2,
                          height: 24,
                        }}
                      />
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#6B7280',
                          lineHeight: 1.6,
                          fontSize: '0.9rem',
                          fontWeight: 500,
                        }}
                      >
                        {partner.description}
                      </Typography>

                      <Box sx={{
                        mt: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: partner.color,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Visit Website
                        </Typography>
                        <ArrowIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>


          </Box>

          {/* ── TEAM SECTION ──────────────────────────────────────────────────── */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip
                label="THE PEOPLE BEHIND PHOSAI"
                sx={{
                  backgroundColor: 'rgba(245,158,11,0.1)', color: '#F59E0B',
                  fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.1em',
                  mb: 2.5, px: 2.5, py: 1, borderRadius: '100px',
                  border: '1px solid rgba(245,158,11,0.2)',
                }}
              />
              <Typography variant="h2" sx={{
                fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: '#111827',
                mb: 2, letterSpacing: '-0.03em', fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                Our Core Team
              </Typography>
              <Typography sx={{
                color: '#6B7280', fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: 560, mx: 'auto', lineHeight: 1.7,
              }}>
                Researchers, engineers, and domain experts united by one mission — making AI work for every African
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
              {[
                {
                  name: 'Bateesa Saul Tobius',
                  role: 'CEO & CO-FOUNDER',
                  expertise: 'Vision · Leadership · Strategic Growth',
                  bio: 'Drives the overall vision and strategic direction of PHOSAI, fostering innovation and partnerships across Africa.',
                  initials: 'BT',
                  image: saulImg,
                  color: '#F59E0B',
                },
                {
                  name: 'Simon Achuka',
                  role: 'Co-Founder and CTO',
                  expertise: 'AI Architecture · ML Systems · Tech Strategy',
                  bio: 'Architects our core AI technologies and oversees the engineering team to deliver scalable language solutions.',
                  initials: 'SA',
                  image: simonImg,
                  color: '#F59E0B',
                },
                {
                  name: 'Brian Otim Dwokatua',
                  role: 'Software Engineer',
                  expertise: 'Full-Stack Development · AI Integration · UI/UX',
                  bio: 'Develops robust software systems that bridge complex AI models with user-friendly interfaces for diverse communities.',
                  initials: 'BO',
                  image: brianImg,
                  color: '#F59E0B',
                },
                {
                  name: 'Jonan Katende',
                  role: 'Marketing Manager',
                  expertise: 'Growth Marketing · Brand Strategy · Community Engagement',
                  bio: 'Leads our marketing efforts, ensuring PHOSAI\'s mission and products reach and empower communities across the continent.',
                  initials: 'JK',
                  image: jonanImg,
                  color: '#F59E0B',
                },
              ].map((member, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Fade in={isVisible} timeout={800 + i * 150}>
                    <Box sx={{
                      p: { xs: 3.5, md: 4 }, borderRadius: '24px', textAlign: 'center',
                      background: 'white', border: `1px solid ${member.color}20`,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                      transition: 'all 0.3s ease', height: '100%',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 24px 60px ${member.color}18`,
                        border: `1px solid ${member.color}40`,
                      },
                    }}>
                      <Avatar 
                        src={member.image}
                        sx={{
                          width: 80, height: 80, mx: 'auto', mb: 2.5,
                          border: `3px solid ${member.color}40`,
                          boxShadow: `0 12px 30px ${member.color}30`,
                        }}
                      >
                        {member.initials}
                      </Avatar>

                      <Typography sx={{
                        fontWeight: 800, color: '#111827', fontSize: '1rem', mb: 0.5,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}>
                        {member.name}
                      </Typography>
                      <Chip label={member.role} size="small" sx={{
                        height: 22, fontSize: '0.68rem', fontWeight: 700, mb: 2,
                        backgroundColor: `${member.color}12`, color: member.color,
                        border: `1px solid ${member.color}25`,
                      }} />

                      <Typography sx={{
                        color: '#6B7280', fontSize: '0.8rem', lineHeight: 1.6,
                        mb: 2, fontWeight: 500,
                      }}>
                        {member.bio}
                      </Typography>

                      <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.06)' }} />

                      <Typography sx={{
                        color: `${member.color}`, fontSize: '0.72rem', fontWeight: 600,
                        letterSpacing: '0.02em',
                      }}>
                        {member.expertise}
                      </Typography>
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            {/* Join the team CTA */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                variant="outlined"
                onClick={() => window.open('mailto:phosaico@gmail.com?subject=Join the PHOSAI Team', '_blank')}
                endIcon={<ArrowIcon />}
                sx={{
                  color: '#F59E0B', border: '2px solid rgba(245,158,11,0.3)', px: 4, py: 1.5,
                  borderRadius: '14px', fontWeight: 700, fontSize: '0.95rem', textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(245,158,11,0.06)',
                    border: '2px solid rgba(245,158,11,0.6)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                We're hiring — Join the team
              </Button>
            </Box>
          </Box>

          {/* Bottom Section with Image and Contact Form */}
          <Box>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} lg={6}>
                <Fade in={isVisible} timeout={1200}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    position: 'relative',
                  }}>
                    <Box sx={{
                      position: 'relative',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
                      transform: 'perspective(1000px) rotateY(-5deg)',
                      transition: 'transform 0.6s ease',
                      '&:hover': {
                        transform: 'perspective(1000px) rotateY(0deg)',
                      }
                    }}>
                      <img 
                        src={wrk} 
                        alt="Machine Translation Technology" 
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '24px',
                        }} 
                      />
                      
                      {/* Floating Badge */}
                      <Box sx={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        background: 'rgba(245,158,11, 0.95)',
                        color: 'white',
                        px: 2,
                        py: 1,
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        backdropFilter: 'blur(10px)',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.05)' }
                        }
                      }}>
                        AI-Powered
                      </Box>
                    </Box>
                  </Box>
                </Fade>
              </Grid>
              
              <Grid item xs={12} lg={6}>
                <Fade in={isVisible} timeout={1500}>
                  <Box ref={emailFormRef}>
                    <EmailFormCard />
                  </Box>
                </Fade>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <Chip
                  label="LET'S CONNECT"
                  sx={{
                    backgroundColor: 'rgba(245,158,11, 0.2)',
                    color: '#F59E0B',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    mb: 3,
                    px: 3,
                    py: 1.5,
                    borderRadius: '20px',
                  }}
                />
                
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900,
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    mb: 4,
                    color: 'white',
                  }}
                >
                  Ready to Partner with PHOSAI?
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    color: '#D1D5DB',
                    maxWidth: 700,
                    margin: '0 auto',
                    fontWeight: 500,
                    mb: 6,
                  }}
                >
                  Whether you're looking to integrate AI solutions or explore partnership opportunities, 
                  our team is ready to discuss how we can work together.
                </Typography>

                {/* Contact Methods */}
                <Grid container spacing={4} sx={{ maxWidth: 800, margin: '0 auto' }}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                          mb: 2,
                        }}
                      >
                        +256 750 371 313
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                        }}
                      >
                        +256 702 560 814
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                          wordBreak: 'break-word',
                        }}
                      >
                        phosaico@gmail.com
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Location
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                        }}
                      >
                        Makerere University
                        <br />
                        Kampala, Uganda
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* CTA Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 3, 
                  flexWrap: 'wrap',
                  mt: 6 
                }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ArrowIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      color: 'white',
                      px: 5,
                      py: 2.5,
                      borderRadius: '20px',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: '0 15px 35px rgba(245,158,11, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 25px 50px rgba(245,158,11, 0.5)',
                        background: 'linear-gradient(135deg, #D97706 0%, #6D28D9 100%)',
                      }
                    }}
                    onClick={() => window.open('mailto:phosaico@gmail.com?subject=Partnership Inquiry - PHOSAI', '_blank')}
                  >
                    Discuss Partnership
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      px: 5,
                      py: 2.5,
                      borderRadius: '20px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 15px 35px rgba(255, 255, 255, 0.2)',
                      }
                    }}
                    onClick={() => window.open('tel:+256750371313', '_blank')}
                  >
                    Call Now
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
